// src/components/Layout.tsx
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Logout,
  Gavel as CasesIcon,
  AccountTree as SchemesIcon,
  Assignment as TasksIcon,
  RateReview as ReviewIcon,
  Search as ExploreIcon,
  RuleFolder as RulesIcon,
  Storage as DatasetsIcon,
  PlayCircleOutline as ScansIcon,
  Inbox as LeadsIcon,
  ExpandMore,
  Settings,
  AccountCircle
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import logo from '../assets/eliotnest-logo.png';
import { NotificationBell } from './NotificationBell';
import AccountSwitcher from './AccountSwitcher';


const Layout = ({ children }: { children: React.ReactNode }) => {
  const { logout, access, user, currentAccount } = useAuth();
  const isDemoMode = currentAccount?.demo_mode ?? false;
  const navigate = useNavigate();
  const location = useLocation();
  const headers = useMemo(() => ({ Authorization: `Bearer ${access}` }), [access]);

  // Scheme dropdown state
  const [schemes, setSchemes] = useState<any[]>([]);
  const [schemesLoading, setSchemesLoading] = useState(false);
  const [schemesAnchorEl, setSchemesAnchorEl] = useState<null | HTMLElement>(null);

  // User dropdown state
  const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null);

  // Load schemes on mount with caching (cache key includes account and user for proper filtering)
  useEffect(() => {
    const accountId = currentAccount?.id || 'none';
    const userRole = user?.role || 'unknown';
    const CACHE_KEY = `eliotnest_schemes_cache_${accountId}_${userRole}`;
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    const fetchSchemes = async () => {
      // Check cache first
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL) {
            setSchemes(data);
            return;
          }
        } catch (e) {
          // Invalid cache, continue to fetch
        }
      }

      setSchemesLoading(true);
      try {
        const res = await api.get('/schemes/', { headers });
        const data = res.data.results ?? res.data;
        setSchemes(data);
        // Cache the result
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
      } catch (err) {
        console.error('Failed to fetch schemes', err);
      } finally {
        setSchemesLoading(false);
      }
    };

    if (access && currentAccount) {
      fetchSchemes();
    }
  }, [access, headers, currentAccount, user?.role]);

  const handleSchemesClick = (event: React.MouseEvent<HTMLElement>) => {
    setSchemesAnchorEl(event.currentTarget);
  };

  const handleSchemesClose = () => {
    setSchemesAnchorEl(null);
  };

  const handleSchemeSelect = (scheme: any) => {
    // Route to scheme by code
    navigate(`/schemes/${scheme.code}`);
    handleSchemesClose();
  };

  const handleUserClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleUserClose = () => {
    setUserAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleUserClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleUserClose();
  };

  // Find the default scheme (three-day-covid-policy)
  const _defaultScheme = schemes.find((s: any) => s.code === 'three-day-covid-policy') || schemes[0];

  // Determine navigation items based on user role.
  // Note: "Review List" is no longer shown to regular users — that surface has
  // been deprecated in favor of the new Explore workbench (Rules / Scans /
  // Leads / Datasets). CASE_REVIEWER role keeps it because reviews are their
  // entire workflow.
  const hiddenInDemo = ['Review List', 'Task List'];
  const allNavItems = user?.role === 'CASE_REVIEWER'
    ? [
        { label: 'Review List', path: '/reviews', icon: <ReviewIcon /> },
      ]
    : user?.role === 'SCHEME_VIEWER'
    ? [] // Scheme viewers only see the Schemes dropdown
    : [
        { label: 'Case List', path: '/cases', icon: <CasesIcon /> },
      ];
  const navItems = isDemoMode
    ? allNavItems.filter(item => !hiddenInDemo.includes(item.label))
    : allNavItems;

  // Explore workbench — available to non-CASE_REVIEWER, non-SCHEME_VIEWER users.
  const showExplore = !!user && user.role !== 'CASE_REVIEWER' && user.role !== 'SCHEME_VIEWER';

  // Determine default route for logo click
  const getDefaultRoute = () => {
    if (user?.role === 'CASE_REVIEWER') return '/reviews';
    if (user?.role === 'SCHEME_VIEWER') return '/schemes';
    return '/home';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top AppBar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
        }}
      >
        <Toolbar>
          {/* Left Side: Logo + Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {/* Logo/Brand */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                }
              }}
              onClick={() => navigate(getDefaultRoute())}
            >
              <img 
                src={logo} 
                alt="EliotNest Logo" 
                style={{ 
                  height: 32, 
                  width: 'auto',
                  objectFit: 'contain'
                }} 
              />
              <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                EliotNest
              </Typography>
            </Box>

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Schemes Dropdown - Hidden for Case Reviewers */}
              {user?.role !== 'CASE_REVIEWER' && (
                <>
                  <Button
                    startIcon={<SchemesIcon />}
                    endIcon={schemesLoading ? <CircularProgress size={16} /> : <ExpandMore />}
                    onClick={handleSchemesClick}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'medium',
                      color: location.pathname.startsWith('/schemes') ? 'primary.main' : 'text.primary',
                      backgroundColor: location.pathname.startsWith('/schemes') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      },
                      borderRadius: 1,
                      px: 2,
                    }}
                    disabled={schemesLoading}
                  >
                    Schemes
                  </Button>

                  <Menu
                    anchorEl={schemesAnchorEl}
                    open={Boolean(schemesAnchorEl)}
                    onClose={handleSchemesClose}
                    PaperProps={{
                      sx: {
                        maxHeight: 400,
                        minWidth: 300,
                        mt: 1,
                      }
                    }}
                  >
                    {/* Show Explore and Prompt Eval options for standard users only */}
                    {user?.role !== 'SCHEME_VIEWER' && [
                        <MenuItem
                          key="workbench"
                          onClick={() => {
                            navigate('/schemes/evaluate');
                            handleSchemesClose();
                          }}
                          sx={{ fontWeight: 'medium' }}
                        >
                          <SchemesIcon sx={{ mr: 1 }} />
                          Workbench
                        </MenuItem>,
                        <MenuItem
                          key="evals-divider"
                          disabled
                          sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            mb: 1,
                            p: 0,
                            minHeight: 0
                          }}
                        />
                    ]}

                    {schemes.map((scheme) => {
                      const isForthcoming = scheme.metadata?.status === 'forthcoming';
                      return (
                        <MenuItem
                          key={scheme.id}
                          onClick={() => !isForthcoming && handleSchemeSelect(scheme)}
                          disabled={isForthcoming}
                          sx={{
                            opacity: isForthcoming ? 0.6 : 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {scheme.name}
                            </Typography>
                            {scheme.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {scheme.description.length > 60
                                  ? `${scheme.description.slice(0, 60)}...`
                                  : scheme.description
                                }
                              </Typography>
                            )}
                          </Box>
                          {isForthcoming && (
                            <Typography variant="caption" color="info.main" sx={{ ml: 1 }}>
                              Coming Soon
                            </Typography>
                          )}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              )}

              {/* Explore — single link into the Rules / Scans / Leads / Datasets workbench */}
              {showExplore && (
                <Button
                  startIcon={<ExploreIcon />}
                  onClick={() => navigate('/explore')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'medium',
                    color: location.pathname.startsWith('/explore') ? 'primary.main' : 'text.primary',
                    backgroundColor: location.pathname.startsWith('/explore') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                    borderRadius: 1,
                    px: 2,
                  }}
                >
                  Explore
                </Button>
              )}

              {navItems.map((item) => (
                <Button
                  key={item.path}
                  startIcon={item.icon}
                  onClick={() => navigate(item.path)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'medium',
                    color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                    backgroundColor: location.pathname === item.path ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                    borderRadius: 1,
                    px: 2,
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Spacer to push user menu to the right */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Right Side: Account Switcher + Notifications + User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountSwitcher />
            <NotificationBell />

            <IconButton
              onClick={handleUserClick}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <AccountCircle />
            </IconButton>
          </Box>

          <Menu
            anchorEl={userAnchorEl}
            open={Boolean(userAnchorEl)}
            onClose={handleUserClose}
            PaperProps={{
              sx: {
                minWidth: 180,
                mt: 1,
              }
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {user?.role !== 'CASE_REVIEWER' && user?.role !== 'SCHEME_VIEWER' && (
              <MenuItem onClick={handleSettings}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Page Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8, // Account for AppBar height
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;